import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { Container, HStack, useResponsive } from '@garnish/core';
import {
  type CloudinaryTransformConfig,
  type ContentfulImageTransformOptions,
  getCloudinaryUrl,
  getContentfulImageUrl,
} from '@garnish/media';
import { useNavbarContainerHeight } from '@garnish/navigation';
import startCase from 'es-toolkit/compat/startCase';

import { Allergen, type AllergenPropertyKind, ModifierType } from '@sg/graphql-schema';
import {
  type DietaryPreferencesContent,
  getIconForAllergen,
  useDietaryPreferencesContent,
  useProductDynamicMicroNutritionData,
} from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { PartnerLineItem } from '@order/shared-line-item';

import { AppFooter, FloatingControls } from '../../../../components';
import { useIsFoldableDevice, useIsLiquidGlassEnabled } from '../../../../hooks';
import {
  type GetSharedLineItemProductModifierGroupsQuery,
  type GetSharedLineItemQuery,
} from '../../GraphQL/ShareLineItemScreen.generated';
import { usePartnerShareLineItemContent } from '../../hooks';
import { ShareLineItemLocationAlert } from '../ShareLineItemLocationAlert';

// ─────────────────────────────────────────────────────────────────────────────

export const PartnerShareLineItemScreen = (props: Props) => {
  const {
    content,
    isAddToBagDisabled,
    isAddingToBag,
    lineItem,
    modifierGroups,
    shouldShowCustomize,
    shouldShowLocationPrompt,
    shouldShowUnavailablePrompt,
    onAddToBag,
    onCustomize,
  } = props;

  const {
    product,
    ingredients: lineItemIngredients,
    calories: lineItemCalories,
    selectedModifiers,
  } = lineItem;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { width: windowWidth } = useWindowDimensions();

  const { currentBreakpoint, minWidth } = useResponsive();
  const { right: safeAreaRight } = useSafeAreaInsets();
  const isFoldableDevice = useIsFoldableDevice();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  const { paddingTopStyle: navbarPaddingTopStyle } = useNavbarContainerHeight();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const dietaryPreferencesContent = useContentfulContentTypeEntry<DietaryPreferencesContent>({
    contentType: 'dietaryPreferences',
  });

  const { icons } = useDietaryPreferencesContent(dietaryPreferencesContent.data?.fields);

  // ─── Shared Values ───────────────────────────────────────────────────

  const scrollOffsetSV = useSharedValue(0);

  // ─── Derived ─────────────────────────────────────────────────────────

  const calories =
    typeof content.caloriesOverride === 'number' ? content.caloriesOverride : lineItemCalories;

  const nutritionData = useProductDynamicMicroNutritionData({
    calories,
    ingredients: lineItemIngredients,
  });

  const partnerImageUrl = content.partnerImage.fields.file?.url;
  const partnerImageLabel = content.partnerImage.fields.description ?? content.partnerName;

  const secondaryImageUrl = content.secondaryImage?.fields.file?.url;
  const productImageUrl = secondaryImageUrl
    ? getContentfulImageUrl(secondaryImageUrl, SECONDARY_IMAGE_OPTIONS)
    : getCloudinaryUrl(product.asset.url, PRODUCT_IMAGE_CONFIG);
  const productImageLabel = content.secondaryImage?.fields.description ?? product.name;

  const buttonMessage = getButtonMessage({
    isAddToBagDisabled,
    shouldShowLocationPrompt,
    shouldShowUnavailablePrompt,
  });
  const buttonLabel = formatMessage(buttonMessage);
  const customizeLabel = formatMessage(messages.customize);
  const unavailableProductMessage = isAddToBagDisabled
    ? formatMessage(messages.unavailableProductForOrderMessage)
    : formatMessage(messages.unavailableProductMessage);

  const ingredients = useMemo(
    () =>
      getIngredientsByGroupOrder({
        ingredients: lineItemIngredients,
        modifierGroups,
        productIngredients: product.ingredients,
        selectedModifiers,
      }),
    [lineItemIngredients, modifierGroups, product.ingredients, selectedModifiers],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasAllergens = product.allergenProperties.length > 0;
  const hasNutritionData = nutritionData.length > 0;
  const hasNutritionNotice = Boolean(content.caloriesOverrideText);
  const hasDetailSections = hasAllergens || hasNutritionData;
  const shouldShowDetails = hasDetailSections || hasNutritionNotice;
  const shouldSeparateDetailSections = hasAllergens && hasNutritionData;
  const detailSectionsPerRow = minWidth.isSM && shouldSeparateDetailSections ? 2 : 1;
  const shouldShowPrompt = shouldShowLocationPrompt || shouldShowUnavailablePrompt;
  const shouldApplyNavbarSpacing = minWidth.isSM;
  const shouldUseCompactGrid = windowWidth <= 500;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetSV.value = event.nativeEvent.contentOffset.y;
    },
    [scrollOffsetSV],
  );

  const handleNavigateBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();

      return;
    }

    router.dismissTo('/');
  }, [router]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const alertContainerStyles = [
    styles.alertContainer,
    currentBreakpoint.isXS ? styles.alertContainerXS : undefined,
  ];
  const heroHeadingFoldableDynamicStyle = useStyle(
    () => ({ paddingRight: safeAreaRight }),
    [safeAreaRight],
  );
  const heroHeadingStyle = [
    currentBreakpoint.isXS && !isFoldableDevice ? styles.heroHeadingXS : undefined,
    currentBreakpoint.isXS && isFoldableDevice ? styles.heroHeadingFoldableXS : undefined,
    currentBreakpoint.isXS && isFoldableDevice ? heroHeadingFoldableDynamicStyle : undefined,
  ];
  const scrollContentContainerStyle = shouldApplyNavbarSpacing ? navbarPaddingTopStyle : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <PartnerLineItem.Container>
      {currentBreakpoint.isXS ? (
        <PartnerLineItem.NavigationHeader
          title={content.productName}
          scrollOffsetSV={scrollOffsetSV}
        />
      ) : null}

      <PartnerLineItem.ScrollView
        contentContainerStyle={scrollContentContainerStyle}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {shouldShowPrompt ? (
          <Container size="medium" style={alertContainerStyles}>
            {shouldShowLocationPrompt ? (
              <ShareLineItemLocationAlert message={formatMessage(messages.selectLocationMessage)} />
            ) : null}

            {shouldShowUnavailablePrompt ? (
              <ShareLineItemLocationAlert message={unavailableProductMessage} palette="caution" />
            ) : null}
          </Container>
        ) : null}

        <PartnerLineItem.Body>
          <PartnerLineItem.Hero.Container>
            <PartnerLineItem.Hero.Content>
              <PartnerLineItem.Hero.Heading style={heroHeadingStyle}>
                <PartnerLineItem.Hero.Title>{content.productName}</PartnerLineItem.Hero.Title>

                <PartnerLineItem.Hero.Byline>
                  <FormattedMessage
                    {...messages.partnerByline}
                    values={{ name: content.partnerName }}
                  />
                </PartnerLineItem.Hero.Byline>
              </PartnerLineItem.Hero.Heading>

              {!minWidth.isSM && partnerImageUrl ? (
                <PartnerLineItem.Hero.Image>
                  <PartnerLineItem.Hero.PartnerImage
                    alt={partnerImageLabel}
                    url={partnerImageUrl}
                  />

                  <PartnerLineItem.Hero.ProductImage
                    url={productImageUrl}
                    alt={productImageLabel}
                    position={content.secondaryImagePosition}
                  />
                </PartnerLineItem.Hero.Image>
              ) : null}

              <PartnerLineItem.Hero.Details>
                <PartnerLineItem.Hero.Description>
                  {content.description}
                </PartnerLineItem.Hero.Description>

                <PartnerLineItem.Hero.ProductName>
                  <FormattedMessage
                    {...messages.customProductName}
                    values={{ name: product.name }}
                  />
                </PartnerLineItem.Hero.ProductName>
              </PartnerLineItem.Hero.Details>

              {minWidth.isSM ? (
                <PartnerLineItem.Hero.Actions>
                  <PartnerLineItem.Hero.Button
                    accessibilityLabel={buttonLabel}
                    disabled={isAddToBagDisabled}
                    isLoading={isAddingToBag}
                    onPress={onAddToBag}
                  >
                    {buttonLabel}
                  </PartnerLineItem.Hero.Button>

                  {shouldShowCustomize ? (
                    <PartnerLineItem.Hero.Button
                      accessibilityLabel={customizeLabel}
                      palette="spinachMuted"
                      style={styles.customizeButton}
                      hoveredStyle={styles.customizeButtonHovered}
                      onPress={onCustomize}
                    >
                      {customizeLabel}
                    </PartnerLineItem.Hero.Button>
                  ) : null}
                </PartnerLineItem.Hero.Actions>
              ) : null}
            </PartnerLineItem.Hero.Content>

            {minWidth.isSM && partnerImageUrl ? (
              <PartnerLineItem.Hero.Image>
                <PartnerLineItem.Hero.PartnerImage alt={partnerImageLabel} url={partnerImageUrl} />

                <PartnerLineItem.Hero.ProductImage
                  alt={productImageLabel}
                  position={content.secondaryImagePosition}
                  url={productImageUrl}
                />
              </PartnerLineItem.Hero.Image>
            ) : null}
          </PartnerLineItem.Hero.Container>

          {ingredients.length > 0 ? (
            <PartnerLineItem.Ingredients
              shouldUseCompactGrid={shouldUseCompactGrid}
              title={formatMessage(messages.ingredientsTitle)}
            >
              {ingredients.map((ingredient) => (
                <PartnerLineItem.Ingredient
                  key={ingredient.id}
                  imageAlt={ingredient.name}
                  imageUrl={ingredient.asset.url}
                  groupDisplayName={ingredient.groupDisplayName}
                  name={ingredient.name}
                  quantity={ingredient.quantity}
                  shouldUseCompactGrid={shouldUseCompactGrid}
                />
              ))}
            </PartnerLineItem.Ingredients>
          ) : null}

          {shouldShowDetails ? (
            <PartnerLineItem.Details>
              {hasDetailSections ? (
                <HStack gap={0} itemsPerRow={detailSectionsPerRow}>
                  {hasNutritionData ? (
                    <PartnerLineItem.Nutrition withBorder={shouldSeparateDetailSections}>
                      {nutritionData.map((nutrition) => (
                        <PartnerLineItem.NutritionItem
                          key={nutrition.label}
                          label={nutrition.label}
                          value={nutrition.value}
                        />
                      ))}
                    </PartnerLineItem.Nutrition>
                  ) : null}

                  {hasAllergens ? (
                    <PartnerLineItem.Allergens withBorder={shouldSeparateDetailSections}>
                      {product.allergenProperties.map((allergen) => {
                        const allergenName = formatAllergenName(allergen.name);
                        const iconUrl = getIconForAllergen(getAllergen(allergen.name), icons);

                        return (
                          <PartnerLineItem.Allergen
                            key={allergen.id}
                            iconAlt={allergenName}
                            iconUrl={iconUrl}
                            name={allergenName}
                          />
                        );
                      })}
                    </PartnerLineItem.Allergens>
                  ) : null}
                </HStack>
              ) : null}

              {content.caloriesOverrideText ? (
                <PartnerLineItem.NutritionNotice>
                  {content.caloriesOverrideText}
                </PartnerLineItem.NutritionNotice>
              ) : null}
            </PartnerLineItem.Details>
          ) : null}
        </PartnerLineItem.Body>

        {minWidth.isSM ? <AppFooter /> : null}
      </PartnerLineItem.ScrollView>

      {currentBreakpoint.isXS ? (
        <PartnerLineItem.FloatingActions>
          <FloatingControls
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            isFullWidthCta
            onBackButtonPress={handleNavigateBack}
          >
            <View style={styles.floatingButtonsContainer}>
              <PartnerLineItem.FloatingButton
                accessibilityLabel={buttonLabel}
                disabled={isAddToBagDisabled}
                isLoading={isAddingToBag}
                onPress={onAddToBag}
              >
                {buttonLabel}
              </PartnerLineItem.FloatingButton>

              {shouldShowCustomize ? (
                <PartnerLineItem.FloatingButton
                  accessibilityLabel={customizeLabel}
                  palette="spinachMuted"
                  style={styles.customizeButton}
                  hoveredStyle={styles.customizeButtonHovered}
                  onPress={onCustomize}
                >
                  {customizeLabel}
                </PartnerLineItem.FloatingButton>
              ) : null}
            </View>
          </FloatingControls>
        </PartnerLineItem.FloatingActions>
      ) : null}
    </PartnerLineItem.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAllergen(allergen: AllergenPropertyKind) {
  return allergen as unknown as Allergen;
}

function formatAllergenName(allergen: AllergenPropertyKind) {
  return startCase(allergen.toLowerCase().replaceAll('_', ' '));
}

function getButtonMessage(params: ButtonMessageParams) {
  const { isAddToBagDisabled, shouldShowLocationPrompt, shouldShowUnavailablePrompt } = params;

  if (shouldShowLocationPrompt) return messages.selectLocation;

  const shouldShowChangeLocation = shouldShowUnavailablePrompt && !isAddToBagDisabled;

  if (shouldShowChangeLocation) return messages.changeLocation;

  return messages.addToBag;
}

function getIngredientsByGroupOrder(params: IngredientsByGroupOrderParams) {
  const { ingredients, modifierGroups, productIngredients, selectedModifiers } = params;

  const groupDetailsByIngredientId = getGroupDetailsByIngredientId(modifierGroups);
  const quantityByIngredientId = getQuantityByIngredientId({ productIngredients, selectedModifiers }); // prettier-ignore

  return getUniqueIngredients(ingredients)
    .map((ingredient) =>
      getIngredientWithGroupDetails({
        groupDetailsByIngredientId,
        ingredient,
        quantity: quantityByIngredientId.get(ingredient.id) ?? SINGLE_PORTION_QUANTITY,
      }),
    )
    .sort(compareIngredientGroupOrder);
}

function getGroupDetailsByIngredientId(modifierGroups: Props['modifierGroups']) {
  const groupDetailsEntries = modifierGroups.flatMap(getGroupDetailsEntries);

  return new Map(groupDetailsEntries);
}

function getGroupDetailsEntries(modifierGroup: ModifierGroup, order: number) {
  const { displayName, modifications } = modifierGroup;

  return modifications.flatMap((modification) => {
    const ingredientId = modification.ingredient?.id;

    if (!ingredientId) {
      return [];
    }

    return [[ingredientId, { displayName, order }] as const];
  });
}

/**
 * The line item repeats an ingredient once per portion, so the list is collapsed
 * to one entry per ingredient, and the portion count comes from the modifiers.
 */
function getUniqueIngredients(ingredients: Props['lineItem']['ingredients']) {
  const ingredientsById = new Map(ingredients.map((ingredient) => [ingredient.id, ingredient]));

  return [...ingredientsById.values()];
}

/**
 * Seeds every default product ingredient with a single portion, then adds one
 * portion per modifier that puts the ingredient back on the plate.
 */
function getQuantityByIngredientId(params: QuantityByIngredientIdParams) {
  const { productIngredients, selectedModifiers } = params;

  const defaultQuantityEntries = productIngredients.map(
    (ingredient) => [ingredient.id, SINGLE_PORTION_QUANTITY] as const,
  );

  return selectedModifiers
    .filter(checkIfPortionModifier)
    .reduce(addIngredientPortion, new Map<string, number>(defaultQuantityEntries));
}

function addIngredientPortion(
  quantityByIngredientId: Map<string, number>,
  modifier: SelectedModifier,
) {
  const ingredientId = modifier.ingredient?.id;

  if (!ingredientId) {
    return quantityByIngredientId;
  }

  const currentQuantity = quantityByIngredientId.get(ingredientId) ?? 0;

  return quantityByIngredientId.set(ingredientId, currentQuantity + 1);
}

function checkIfPortionModifier(modifier: SelectedModifier) {
  const { modifierType } = modifier;

  return modifierType === ModifierType.Addition || modifierType === ModifierType.Substitution;
}

function getIngredientWithGroupDetails(params: IngredientWithGroupDetailsParams) {
  const { groupDetailsByIngredientId, ingredient, quantity } = params;

  const groupDetails = groupDetailsByIngredientId.get(ingredient.id);

  return {
    ...ingredient,
    groupDisplayName: groupDetails?.displayName,
    groupOrder: groupDetails?.order ?? UNGROUPED_MODIFIER_GROUP_ORDER,
    quantity,
  };
}

function compareIngredientGroupOrder(
  firstIngredient: IngredientWithGroupDetails,
  secondIngredient: IngredientWithGroupDetails,
) {
  return firstIngredient.groupOrder - secondIngredient.groupOrder;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const UNGROUPED_MODIFIER_GROUP_ORDER = Number.MAX_SAFE_INTEGER;

const SINGLE_PORTION_QUANTITY = 1;

const PRODUCT_IMAGE_SIZE = 120;

const PRODUCT_IMAGE_CONFIG: CloudinaryTransformConfig = {
  width: PRODUCT_IMAGE_SIZE,
};

const SECONDARY_IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  w: PRODUCT_IMAGE_SIZE * 2,
  fm: 'webp',
  q: 70,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  alertContainer: {
    marginTop: theme.spacing['4'],
  },
  alertContainerXS: {
    marginBottom: theme.spacing['6'],
  },
  heroHeadingXS: {
    alignItems: 'center',
    marginBottom: theme.spacing['2'],
  },
  heroHeadingFoldableXS: {
    alignSelf: 'stretch',
    alignItems: 'flex-start',
    marginBottom: theme.spacing['2'],
  },
  floatingButtonsContainer: {
    flexDirection: 'row',
    gap: theme.spacing['2'],
  },
  customizeButton: {
    backgroundColor: '#dde2d5',
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
  customizeButtonHovered: {
    backgroundColor: '#d4d9cb',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly partnerByline: { readonly name: MessageValue };
  readonly customProductName: { readonly name: MessageValue };
  readonly addToBag: NoMessageValues;
  readonly changeLocation: NoMessageValues;
  readonly customize: NoMessageValues;
  readonly ingredientsTitle: NoMessageValues;
  readonly selectLocationMessage: NoMessageValues;
  readonly selectLocation: NoMessageValues;
  readonly unavailableProductMessage: NoMessageValues;
  readonly unavailableProductForOrderMessage: NoMessageValues;
}>({
  partnerByline: {
    defaultMessage: 'By {name}',
    description: 'Partner share line item | Partner byline',
  },
  customProductName: {
    defaultMessage: 'Custom {name}',
    description: 'Partner share line item | Underlying custom product name',
  },
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Partner share line item | Add to bag CTA without price',
  },
  changeLocation: {
    defaultMessage: 'Change location',
    description: 'Partner share line item | Change location CTA',
  },
  customize: {
    defaultMessage: 'Customize',
    description: 'Partner share line item | Customize CTA',
  },
  ingredientsTitle: {
    defaultMessage: 'In This Custom',
    description: 'Partner share line item | Ingredients heading',
  },
  selectLocationMessage: {
    defaultMessage: 'Select a location to order this shared item.',
    description: 'Partner share line item | Select location message',
  },
  selectLocation: {
    defaultMessage: 'Select location',
    description: 'Partner share line item | Select location CTA',
  },
  unavailableProductMessage: {
    defaultMessage: 'This item is not available at your selected location.',
    description: 'Partner share line item | Unavailable product message',
  },
  unavailableProductForOrderMessage: {
    defaultMessage: 'This item is not available for order at your selected location.',
    description: 'Partner share line item | Unavailable product for order message',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  content: NonNullable<ReturnType<typeof usePartnerShareLineItemContent>['content']>;
  isAddToBagDisabled: boolean;
  isAddingToBag: boolean;
  lineItem: SharedLineItemQueryLineItem;
  modifierGroups: SharedLineItemProductModifierGroups;
  onAddToBag: () => void;
  onCustomize: () => void;
  shouldShowCustomize: boolean;
  shouldShowLocationPrompt: boolean;
  shouldShowUnavailablePrompt: boolean;
};

type IngredientsByGroupOrderParams = {
  ingredients: Props['lineItem']['ingredients'];
  modifierGroups: Props['modifierGroups'];
  productIngredients: Props['lineItem']['product']['ingredients'];
  selectedModifiers: Props['lineItem']['selectedModifiers'];
};

type QuantityByIngredientIdParams = Pick<
  IngredientsByGroupOrderParams,
  'productIngredients' | 'selectedModifiers'
>;

type SelectedModifier = Props['lineItem']['selectedModifiers'][number];

type IngredientWithGroupDetailsParams = {
  groupDetailsByIngredientId: ReturnType<typeof getGroupDetailsByIngredientId>;
  ingredient: Props['lineItem']['ingredients'][number];
  quantity: number;
};

type IngredientWithGroupDetails = ReturnType<typeof getIngredientWithGroupDetails>;

type ModifierGroup = Props['modifierGroups'][number];

type ButtonMessageParams = Pick<
  Props,
  'isAddToBagDisabled' | 'shouldShowLocationPrompt' | 'shouldShowUnavailablePrompt'
>;

type SharedLineItemQueryLineItem = Extract<
  GetSharedLineItemQuery['lineItemBySlug'],
  { __typename: 'LineItemSuccess' }
>['lineItem'];

type SharedLineItemProductModifierGroups =
  GetSharedLineItemProductModifierGroupsQuery['product']['modifierGroups'];
